// This file is part of ecbyts/ECBT (Environmental & Occupational Core Byte Tools).
// Copyright (C) 2026 Calvin Stefan Iost
// Licensed under the GNU Affero General Public License v3.0 (AGPL-3.0-only).
// See LICENSE file or https://www.gnu.org/licenses/agpl-3.0.txt

/**
 * tabThumbnailStore.ts — TAB-MODEL-SWITCH-001 RF-10 (miniatura por aba, em memória).
 *
 * Cada troca de aba gera a miniatura da cena (via `captureTabThumbnail`, que
 * já reduz ao teto de 30KB — a convenção de tamanho vive lá e em
 * `_snapshotPersistencePure.ts`; este store NÃO cria um segundo teto).
 * A miniatura da mesma aba é SOBRESCRITA na troca seguinte — sempre o último
 * frame visto vale, nunca acumula histórico.
 *
 * Aba sem cena (3D nunca aberto, captura recusada) fica com dataUrl null e a
 * RAZÃO nomeada — o consumidor de UI mostra um placeholder nomeado, jamais
 * uma <img> quebrada. `null`/ausente significa "sem miniatura", nunca erro.
 */

export interface TabThumbnailEntry {
    /** PNG data: URL (≤ teto do snapshot) ou null quando não há miniatura. */
    readonly dataUrl: string | null;
    /** Motivo nomeado quando dataUrl é null (ex.: 'no_scene'). */
    readonly reason: string;
    /** Momento da última atualização (Date.now()). */
    readonly updatedAt: number;
}

/** Motivo canônico de placeholder quando a captura não produziu imagem. */
export const THUMBNAIL_PLACEHOLDER_REASON = 'no_scene';

const _store = new Map<string, TabThumbnailEntry>();

/**
 * Grava (ou sobrescreve) a miniatura da aba. `dataUrl` null registra o
 * placeholder com motivo nomeado — presença no store = "já tentamos".
 */
export function setTabThumbnail(tabId: string, dataUrl: string | null, reason?: string): void {
    if (!tabId) return;
    _store.set(tabId, {
        dataUrl: typeof dataUrl === 'string' && dataUrl.length > 0 ? dataUrl : null,
        reason: dataUrl ? 'captured' : reason || THUMBNAIL_PLACEHOLDER_REASON,
        updatedAt: Date.now(),
    });
}

/** Miniatura da aba, ou null se nunca foi tentada. */
export function getTabThumbnail(tabId: string): TabThumbnailEntry | null {
    return _store.get(tabId) || null;
}

/** Remove a entrada (aba fechada). */
export function deleteTabThumbnail(tabId: string): void {
    _store.delete(tabId);
}

/** Limpa tudo (testes). */
export function clearTabThumbnails(): void {
    _store.clear();
}
